/* eslint-disable react/prop-types */
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, X, Mail, Phone, ChevronRight } from "lucide-react";
import { CSSTransition } from "react-transition-group";
import CreatePopup from "../Components/CreatePopup";

const ManagerCard = ({ manager, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
  >
    <div className="flex justify-between items-start mb-4">
      <h3 className="font-semibold text-lg">
        {manager.last_name} {manager.first_name}
      </h3>
      <ChevronRight className="text-gray-400" size={20} />
    </div>

    <div className="space-y-3">
      <div className="flex items-center gap-3 text-gray-600">
        <Mail size={16} className="flex-shrink-0" />
        <span className="text-sm truncate">{manager.email}</span>
      </div>
      <div className="flex items-center gap-3 text-gray-600">
        <Phone size={16} />
        <span className="text-sm">{manager.phone}</span>
      </div>
    </div>
  </div>
);

const Notification = ({ message }) => (
  <div className="p-4 shadow-xl z-50 bg-white fixed right-3 top-10 border-l-4 border-red-500 rounded-md flex items-center gap-4">
    <span className="flex items-center gap-4">
      <X className="text-red-500" />
      <p className="font-light">{message}</p>
    </span>
  </div>
);

const Managers = () => {
  const [edit, setEdit] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotification, setshowNotification] = useState(false);
  const editRef = useRef(null);
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_API_URL;
  const token = JSON.parse(localStorage.getItem("user"))?.access;

  const styles = {
    enter: "transform translate-y-full opacity-0",
    enterActive:
      "transform -translate-y-0 opacity-100 transition-all duration-500 ease-in-out",
    exitActive:
      "transform translate-y-full opacity-0 transition-all duration-500 ease-in-out",
  };

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiUrl}/api/v1/auth/users/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch managers");

        const data = await response.json();
        const filteredManagers = data.data.filter(
          (user) => user.user_type === "MANAGER"
        );
        setManagers(filteredManagers);
      } catch (error) {
        console.error("Error fetching managers:", error);
        setshowNotification("An error occurred. Please try again.");
        setTimeout(() => setshowNotification(false), 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchManagers();
  }, [apiUrl, token]);

  return (
    <div className="bg-gray-50 md:ml-[350px] min-h-screen">
      <div className="p-6">
        <div className="bg-white flex items-center justify-between p-4 mb-6 rounded-lg shadow-sm">
          <h2 className="font-bold text-xl text-gray-800">All Managers</h2>
          <button
            onClick={() => setEdit(true)}
            className="bg-btn hover:bg-btn/80 text-white px-4 py-2 flex items-center gap-2 rounded-lg transition-colors"
          >
            <span>Add Manager</span>
            <UserPlus size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-btn border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : managers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600">
              You have not added any managers yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managers.map((manager) => (
              <ManagerCard
                key={manager.id}
                manager={manager}
                onClick={() => navigate(`/managers/${manager.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <CreatePopup
        styles={styles}
        edit={edit}
        editRef={editRef}
        setEdit={setEdit}
      />

      <CSSTransition
        in={showNotification}
        classNames={styles}
        timeout={500}
        unmountOnExit
      >
        <Notification message={showNotification} />
      </CSSTransition>
    </div>
  );
};

export default Managers;
